import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { ToastContainer, toast } from "react-toastify";
import { BsFillSuitHeartFill, BsShareFill } from "react-icons/bs";
import { FaStar, FaFacebookF, FaTwitter, FaPinterest } from "react-icons/fa";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";

// Components
import Searchbar from "./Shearchbar";
import Navbar from "./Navbar";
import Featuredproducts from "./Featuredproducts";
import Newarrivals from "./Newarrivals";
import Footer from "./Footer";
import Dealsoftheday from "./Dealsoftheday";
import Manubar from "./Manubar";

// Styles
import "./m.css";
import "./loader.css";
import "react-toastify/dist/ReactToastify.css";

const Single_products = () => {
  // States
  const navigate = useNavigate();
  const { id } = useParams();
  const [slideIndex, setSlideIndex] = useState(0);
  const [images, setImages] = useState([]);
  const [productDetails, setProductDetails] = useState([]);
  const [counter, setCounter] = useState(1);
  const [indexValue, setIndexValue] = useState(0);
  const [cardStatus, setCardStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);

  // Get data from storage
  const token = secureLocalStorage.getItem("n_token");
  const productsDetails = id;
  const cartListId = secureLocalStorage.getItem("n_cartlist_id");
  const _id = secureLocalStorage.getItem("n_id");

  // Image slider functions
  const plusSlides = (n) => {
    const newIndex = (slideIndex + n + images.length) % images.length;
    setSlideIndex(newIndex);
  };

  const currentSlide = (n) => {
    setSlideIndex(n);
  };

 // Fetch product details
const fetchProductDetails = useCallback(async () => {
  if (!productsDetails) {
    toast.error("Product ID is missing");
    return;
  }

  const controller = new AbortController();
  try {
    setLoading(true);
    
    // Prepare payload - ONLY product_id is required
    const payload = {
      product_id: productsDetails,
    };
    
    // Add user_id ONLY if user is logged in AND _id exists
    // Otherwise, don't send user_id at all
    if (_id && _id.trim() !== "") {
      payload.user_id = _id;
    }

    const res = await axios.post(
      `${process.env.REACT_APP_API}website/product_details`,
      payload,
      {
        // Send token header only if token exists and is not empty
        headers: (token && token.trim() !== "") ? { token } : {},
        signal: controller.signal,
      }                          
    );

    if (res?.data?.result) {
      setImages(res?.data?.data?.[0]?.product_image || []);
      setProductDetails(res.data.data);
    } else {
      toast.error("Failed to load product details");
    }
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error("Error fetching product:", error);
      toast.error("Error loading product details");
    }
  } finally {
    setLoading(false);
  }

  return () => controller.abort();
}, [productsDetails, _id, token]);

useEffect(() => {
  fetchProductDetails();
}, [fetchProductDetails]);
  // Quantity selection
  const handleQuantitySelect = (index) => {
    setIndexValue(index);
  };

  // Price calculation
  const counterPrice = productDetails[0]?.price?.[indexValue] * counter;
  const savings = productDetails[0]?.mrp - counterPrice;
  const savingsPercentage = productDetails[0]?.mrp
    ? Math.round((savings / productDetails[0].mrp) * 100)
    : 0;

  // Counter functions
  const increment = () => setCounter((prev) => prev + 1);
  const decrement = () => setCounter((prev) => (prev > 1 ? prev - 1 : 1));

  // Add to cart
  const addToCart = async (modelData) => {
    if (!token) {
      toast.error("Please login to add items to cart");
      return;
    }

    if (addingToCart) return;

    try {
      setAddingToCart(true);
      const data = {
        cart_id: cartListId,
        user_id: _id,
        product_id: modelData?.[0]?._id,
        quantity: modelData?.[0]?.quantity?.[indexValue],
        no_of_product: counter,
        mrp: modelData?.[0]?.mrp,
        price: modelData[0]?.price?.[indexValue],
        total_mrp_ammount: modelData?.[0]?.mrp,
        total_price_ammount: counterPrice,
        product_name: modelData?.[0]?.product_name,
        product_image: modelData?.[0]?.product_image?.[0],
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API}website/add_to_cart`,
        data,
        { headers: { token } }
      );

      if (res.data.result) {
        setCardStatus(true);
        toast.success("✅ " + res.data.msg);
        secureLocalStorage.setItem("n_cartlist_id", res.data.data._id);
        fetchProductDetails();
      } else {
        toast.error("❌ Failed to add item to cart");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error("❌ Error adding item to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  const addToFavorites = async (productId, favStatus) => {
    if (!token) {
      toast.info("Please login to add favorites");
      return;
    }

    try {
      const items = {
        user_id: _id,
        product_id: productId,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API}website/add_favourite_product`,
        items,
        { headers: { token } }
      );
      console.log(res);

      if (res.data.result) {
        toast.success("❤️ Product added to favourites successfully.");
        // Refresh product details to update favorite status
        fetchProductDetails();
      }
    } catch (error) {
      console.error("Favorite error:", error);
      if (error.response.status === 400) {
        toast.error("Product removed from favourites successfully.");
        fetchProductDetails();
      }
    }
  };

  // Share product
  const shareProduct = () => {
    if (navigator.share) {
      navigator.share({
        title: productDetails[0]?.product_name,
        text: productDetails[0]?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("📋 Product link copied to clipboard!");
    }
  };

  // Render stars for rating
  const renderStars = (rating = 5) => {
    return Array.from({ length: 5 }, (_, index) => (
      <FaStar
        key={index}
        className={index < rating ? "text-warning" : "text-muted"}
        size={16}
      />
    ));
  };

if (loading) {
    return (
        <div style={{
            border: '2px solid red', 
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
             <div className="loader"></div>
        </div>
    );
}

 
  return (
    <div className="single-product-page">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header Section */}
      <div className="modal fade offcanvas-modal" id="exampleModal">
        <div className="modal-dialog offcanvas-dialog">
          <div className="modal-content">
            <Manubar />
          </div>
        </div>
      </div>

      <header>
        <Searchbar />
        <Navbar />
      </header>

      {/* Breadcrumb */}
      <nav className="breadcrumb-section bg-light py-3">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <ol className="breadcrumb m-0 p-0 align-items-center">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-decoration-none text-muted">
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link
                    to="/shop"
                    className="text-decoration-none text-muted"
                  >
                    Products
                  </Link>
                </li>
                <li className="breadcrumb-item active text-dark fw-semibold">
                  {productDetails[0]?.product_name}
                </li>
              </ol>
            </div>
          </div>
        </div>
      </nav>

      {/* Product Details */}
      {productDetails?.length > 0 ? (
        <section className="py-5">
          <div className="container">
            <div className="row g-5">
              {/* Product Images */}
              <div className="col-lg-6">
                <div className="product-gallery" style={{ height: "100%" }}>
                  <div
                    className="main-image position-relative bg-white rounded-3 shadow-sm p-3"
                    style={{ height: "78%" }}
                  >
                    {productDetails[0]?.offer_percent > 0 && (
                      <span className="badge bg-danger position-absolute top-0 start-0 m-2 fs-6">
                        -{productDetails[0]?.offer_percent}%
                      </span>
                    )}

                    <div className="image-container text-center">
                      <img
                        className="img-fluid rounded-2"
                        src={`${process.env.REACT_APP_API}uploads/${images[slideIndex]}`}
                        alt={productDetails[0]?.product_name}
                        style={{
                          maxHeight: "500px",
                          width: "auto",
                          objectFit: "contain",
                        }}
                      />
                    </div>

                    <button
                      className="btn btn-light position-absolute top-50 start-0 translate-middle-y rounded-circle shadow"
                      onClick={() => plusSlides(-1)}
                      style={{ width: "45px", height: "45px", padding: "0px" }}
                    >
                      <HiChevronLeft size={20} />
                    </button>
                    <button
                      className="btn btn-light position-absolute top-50 end-0 translate-middle-y rounded-circle shadow"
                      onClick={() => plusSlides(1)}
                      style={{ width: "45px", height: "45px", padding: "0px" }}
                    >
                      <HiChevronRight size={20} />
                    </button>

                    <div className="image-counter position-absolute bottom-0 start-50 translate-middle-x mb-2">
                      <span className="badge bg-dark bg-opacity-75 px-3 py-2">
                        {slideIndex + 1} / {images.length}
                      </span>
                    </div>
                  </div>

                  {/* Thumbnails */}
                  {images.length > 1 && (
                    <div className="thumbnails mt-3">
                      <div className="row g-2 justify-content-center">
                        {images.map((image, index) => (
                          <div className="col-auto" key={index}>
                            <img
                              className={`thumbnail img-fluid rounded-2 cursor-pointer ${
                                index === slideIndex
                                  ? "active border border-primary"
                                  : ""
                              }`}
                              src={`${process.env.REACT_APP_API}uploads/${image}`}
                              alt={`Thumbnail ${index + 1}`}
                              onClick={() => currentSlide(index)}
                              style={{
                                width: "80px",
                                height: "80px",
                                objectFit: "cover",
                                opacity: index === slideIndex ? 1 : 0.7,
                                transition: "all 0.3s ease",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Info */}
              <div className="col-lg-6">
                <div className="product-info">
                  <h1 className="h2 fw-bold text-dark mb-2">
                    {productDetails[0]?.product_name}
                  </h1>

                  <div className="category-text text-muted mb-3">
                    <strong>Category:</strong>{" "}
                    {productDetails[0]?.category_name}
                  </div>

                  {/* Ratings */}
                  <div className="ratings mb-3 d-flex align-items-center">
                    <div className="stars me-2">{renderStars()}</div>
                    <span className="text-muted">(5 Reviews)</span>
                  </div>

                  {/* Pricing */}
                  <div className="pricing-section mb-4">
                    <div className="d-flex align-items-center flex-wrap gap-3 mb-2">
                      <span className="h3 fw-bold text-primary mb-0">
                        ₹{counterPrice.toFixed(2)}
                      </span>
                      <span className="h5 text-muted text-decoration-line-through mb-0">
                        ₹{productDetails[0]?.mrp?.toFixed(2)}
                      </span>
                      {savings > 0 && (
                        <span className="badge bg-success fs-6">
                          Save ₹{savings.toFixed(2)} ({savingsPercentage}%)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="description-section mb-4">
                    <p
                      className="text-muted mb-0"
                      style={{ textAlign: "start" }}
                    >
                      {productDetails[0]?.description}
                    </p>
                  </div>

                  {/* Quantity Options */}
                  <div className="quantity-options mb-4">
                    <label className="form-label fw-semibold">
                      Select Quantity:
                    </label>
                    <div className="d-flex flex-wrap gap-2">
                      {productDetails[0]?.quantity?.map((item, index) => (
                        <button
                          key={index}
                          className={`btn ${
                            index === indexValue
                              ? "btn-primary"
                              : "btn-outline-primary"
                          } rounded-pill`}
                          onClick={() => handleQuantitySelect(index)}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Add to Cart Section */}
                  <div className="add-to-cart-section mb-4">
                    <div className="row g-3 align-items-center">
                      <div className="col-auto">
                        <label className="form-label fw-semibold mb-0">
                          Qty:
                        </label>
                      </div>
                      <div className="col-auto">
                        <div className="quantity-selector d-flex align-items-center border rounded-3">
                          <button
                            className="btn btn-light border-0 px-3 py-2"
                            onClick={decrement}
                            disabled={counter <= 1}
                          >
                            -
                          </button>
                          <span className="px-3 py-2 fw-semibold">
                            {counter}
                          </span>
                          <button
                            className="btn btn-light border-0 px-3 py-2"
                            onClick={increment}
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="col">
                        {token ? (
                          productDetails[0]?.cart_status === "1" ? (
                            <button
                              onClick={() => navigate("/cardlist")}
                              className="btn btn-success btn-lg w-100 py-3"
                            >
                              Already in Cart (Go to Cart)
                            </button>
                          ) : (
                            <button
                              className="btn btn-dark btn-lg w-100 py-3"
                              onClick={() => addToCart(productDetails)}
                              disabled={addingToCart}
                            >
                              {addingToCart ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Adding...
                                </>
                              ) : (
                                "Add to Cart"
                              )}
                            </button>
                          )
                        ) : (
                          <Link
                            to="/login"
                            className="btn btn-dark btn-lg w-100 py-3 text-decoration-none"
                          >
                            Login to Add to Cart
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="action-buttons d-flex gap-3 mb-4">
                    {/* Wishlist */}
                    <div className="wishlist-btn">
                      {token ? (
                        productDetails[0]?.favourite_status === "1" ? (
                          <button
                            className="btn btn-outline-danger rounded-circle p-3"
                            onClick={() =>
                              addToFavorites(
                                productDetails[0]?._id,
                                productDetails[0]?.favourite_status
                              )
                            }
                            title="Remove from wishlist"
                          >
                            <BsFillSuitHeartFill size={20} />
                          </button>
                        ) : (
                          <button
                            className="btn btn-outline-secondary rounded-circle p-3"
                            onClick={() =>
                              addToFavorites(
                                productDetails[0]?._id,
                                productDetails[0]?.favourite_status
                              )
                            }
                            title="Add to wishlist"
                          >
                            <BsFillSuitHeartFill size={20} />
                          </button>
                        )
                      ) : (
                        <Link
                          to="/login"
                          className="btn btn-outline-secondary rounded-circle p-3"
                          title="Login to add to wishlist"
                        >
                          <BsFillSuitHeartFill size={20} />
                        </Link>
                      )}
                    </div>

                    {/* Share */}
                    <button
                      className="btn btn-outline-primary rounded-circle p-3"
                      onClick={shareProduct}
                      title="Share product"
                    >
                      <BsShareFill size={20} />
                    </button>
                  </div>

                  {/* Social Sharing */}
                  {/* <div className="social-sharing">
                    <span className="text-muted me-3">Share:</span>
                    <div className="btn-group">
                      <button className="btn btn-outline-primary btn-sm">
                        <FaFacebookF />
                      </button>
                      <button className="btn btn-outline-info btn-sm">
                        <FaTwitter />
                      </button>
                      <button className="btn btn-outline-danger btn-sm">
                        <FaPinterest />
                      </button>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <div className="text-center py-5">
          <div className="loader"></div>
          <p className="text-muted mt-3">Loading product details...</p>
        </div>
      )}

      <section className="service-section section-mt section-mb">
        <div className="container">
          <div className="services">
            <div className="row mb-n7">
              <div className="col-sm-6 col-lg-3 mb-7">
                <div className="service-item">
                  <img
                    className="service-icon"
                    src="/assets/images/icon/service1.png"
                    alt="images_not_found"
                  />
                  <h3 className="service-title">100% SECURE PAYMENTS</h3>
                  <p>Moving your card details to a much more secured place</p>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-7">
                <div className="service-item">
                  <img
                    className="service-icon"
                    src="/assets/images/icon/service2.png"
                    alt="images_not_found"
                  />
                  <h3 className="service-title">TRUSTPAY</h3>
                  <p>100% Payment Protection. Easy Return Policy</p>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-7">
                <div className="service-item">
                  <img
                    className="service-icon"
                    src="/assets/images/icon/service3.png"
                    alt="images_not_found"
                  />
                  <h3 className="service-title">HELP CENTER</h3>
                  <p>
                    Got a question? Look no further.Browse our FAQs or submit
                    your query here.
                  </p>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-7">
                <div className="service-item">
                  <img
                    className="service-icon"
                    src="/assets/images/icon/service4.png"
                    alt="images_not_found"
                  />
                  <h3 className="service-title">Express Shipping</h3>
                  <p>Fast, reliable delivery from global warehouses</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Single_products;
